/**
 * Searchable combobox for selecting a product from the universal catalog.
 * Includes an "Add new product..." option at the bottom to trigger inline
 * creation when the desired product doesn't exist yet.
 *
 * Currently loads the full catalog upfront and relies on Base UI's
 * client-side filtering. TODO: Switch to async server-side search
 * (via `onInputValueChange` + debounce) once the catalog grows.
 */
import { Plus, TriangleAlert } from 'lucide-react'

import type { CatalogProduct } from '@snootsvet/shared'

import {
  Combobox,
  ComboboxCollection,
  ComboboxContent,
  ComboboxEmpty,
  ComboboxInput,
  ComboboxItem,
  ComboboxList,
  ComboboxSeparator,
} from '@/frontend/components/ui/combobox'
import { useListCatalogProducts } from '@/frontend/features/inventory/use-list-catalog-products'

// ---------------------------------------------------------------------------
// Combobox item types
// ---------------------------------------------------------------------------

type CatalogOption = { type: 'product'; product: CatalogProduct } | { type: 'add_new' }

function getOptionKey(option: CatalogOption): string {
  return option.type === 'product' ? option.product.id : option.type
}

function getOptionLabel(option: CatalogOption): string {
  if (option.type === 'product') {
    const { name, formulation } = option.product
    return `${name} – ${formulation}`
  }
  return 'Add new medication...'
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

interface CatalogProductSearchProps {
  id?: string
  /** The selected catalog product ID (or null if none selected). */
  value: string | null
  /** Called with the catalog product ID on selection, or null when cleared / "Add new" is picked. */
  onChange: (catalogProductId: string | null) => void
  /** Called when the user selects "Add new product...". */
  onAddNew: () => void
  onBlur?: () => void
  disabled?: boolean
  placeholder?: string
  showClear?: boolean
  'aria-invalid'?: boolean
}

function CatalogProductSearch({
  id,
  value,
  onChange,
  onAddNew,
  onBlur,
  disabled,
  placeholder = 'Search medications...',
  showClear = true,
  ...props
}: CatalogProductSearchProps) {
  const { data: catalogProducts, isLoading } = useListCatalogProducts()

  const options: CatalogOption[] = [
    ...(catalogProducts?.map((p) => ({ type: 'product' as const, product: p })) ?? []),
    { type: 'add_new' as const },
  ]

  const selected = value
    ? (options.find((o) => o.type === 'product' && o.product.id === value) ?? null)
    : null

  return (
    <Combobox<CatalogOption>
      value={selected}
      onValueChange={(option) => {
        if (!option) {
          onChange(null)
        } else if (option.type === 'add_new') {
          onChange(null)
          onAddNew()
        } else {
          onChange(option.product.id)
        }
      }}
      isItemEqualToValue={(a, b) => getOptionKey(a) === getOptionKey(b)}
      itemToStringLabel={getOptionLabel}
      filter={(value: CatalogOption, query: string) => {
        // "Add new" is always visible
        if (value.type === 'add_new') return true
        return getOptionLabel(value).toLowerCase().includes(query.toLowerCase())
      }}
      items={options}
      disabled={disabled}
    >
      <ComboboxInput
        id={id}
        placeholder={placeholder}
        onBlur={onBlur}
        aria-invalid={props['aria-invalid']}
        showClear={showClear}
      />
      <ComboboxContent>
        <ComboboxList>
          {isLoading ? (
            <div className="py-2 text-center text-muted-foreground">Loading...</div>
          ) : (
            <>
              <ComboboxEmpty>No medications found.</ComboboxEmpty>
              <ComboboxCollection>
                {(option) =>
                  option.type === 'add_new' ? (
                    <>
                      <ComboboxSeparator />
                      <ComboboxItem key="add_new" value={option}>
                        <span className="flex items-center gap-1.5 text-primary">
                          <Plus className="size-3.5" />
                          Add new medication...
                        </span>
                      </ComboboxItem>
                    </>
                  ) : (
                    <ComboboxItem key={option.product.id} value={option}>
                      <div className="flex flex-col">
                        <div className="flex items-center gap-1.5">
                          <span>{option.product.name}</span>
                          {option.product.isControlledSubstance && (
                            <TriangleAlert className="size-3.5 shrink-0 text-warning-fg" />
                          )}
                        </div>
                        <span className="text-sm text-muted-foreground">
                          {option.product.formulation}
                          {option.product.unit ? ` · ${option.product.unit}` : ''}
                        </span>
                      </div>
                    </ComboboxItem>
                  )
                }
              </ComboboxCollection>
            </>
          )}
        </ComboboxList>
      </ComboboxContent>
    </Combobox>
  )
}

export { CatalogProductSearch }
